import { useCallback } from "react";
import styled from "@emotion/styled";
import {
  MDSButton,
  MDSButtonVariant,
  MDSIconButton,
  MDSIconButtonSize,
  MDSIconKind,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
  MDSTooltip,
  MDSTooltipPlacement,
} from "@/mds";
import { RecordingStatus } from "@/modules/audio-recording/legacy";
import type {
  AudioCaptureRecordingState,
  AudioCaptureTransformStatus,
  AudioCaptureUploadStatus,
} from "../audio-capture-actions.types";
import { getProcessingLabel } from "../audio-capture-actions.utils";
import { AudioCaptureWaveform } from "./audio-capture-waveform";
import { useSyntheticProgress } from "../use-synthetic-progress";

interface AudioCaptureToolbarProps {
  recording: AudioCaptureRecordingState;
  isMicrophoneDisconnected: boolean;
  onPause: () => Promise<boolean | void> | void;
  onResume: () => Promise<boolean | void> | void;
  onStart: () => void;
  onClear: () => void;
  onFinish: () => Promise<void>;
  onRetryUpload: () => Promise<void>;
  onRetryTransform: () => Promise<void>;
  uploadStatus: AudioCaptureUploadStatus;
  transformStatus: AudioCaptureTransformStatus;
}

export function AudioCaptureToolbar({
  recording,
  isMicrophoneDisconnected,
  onPause,
  onResume,
  onStart,
  onClear,
  onFinish,
  onRetryUpload,
  onRetryTransform,
  uploadStatus,
  transformStatus,
}: AudioCaptureToolbarProps) {
  const handleDoneClick = useCallback(async () => {
    await onFinish();
  }, [onFinish]);

  const showSyntheticProgress =
    recording.isProcessing &&
    recording.uploadProgressPercent === null &&
    transformStatus.state !== "error";
  const syntheticPercent = useSyntheticProgress(showSyntheticProgress);

  const showRetryUpload = uploadStatus.state === "error";
  const showRetryTransform = transformStatus.state === "error";
  const disableFinishButton =
    !recording.canProcessRecording ||
    recording.isProcessing ||
    showRetryUpload ||
    showRetryTransform;

  return (
    <StyledButtonsContainer $showSeparator={true}>
      <LeftActions>
        {recording.status === RecordingStatus.RECORDING && (
          <StyledIconButton
            onClick={onPause}
            isDisabled={isMicrophoneDisconnected}
            size={MDSIconButtonSize.Medium}
            iconKind={MDSIconKind.PauseSolid}
          />
        )}
        {recording.status === RecordingStatus.PAUSED && (
          <StyledResumeButton
            variant={MDSButtonVariant.OnAccent}
            onClick={onResume}
            isDisabled={isMicrophoneDisconnected}
          >
            Resume
          </StyledResumeButton>
        )}
        {recording.status === RecordingStatus.LOCKED && (
          <StyledIconButton
            iconKind={MDSIconKind.MicrophoneAlt}
            size={MDSIconButtonSize.Medium}
            onClick={() => {
              onStart();
            }}
            isDisabled={recording.isFinished || isMicrophoneDisconnected}
          />
        )}
        {recording.isProcessing && (
          <StyledProcessingButton variant={MDSButtonVariant.OnAccent} isDisabled={true}>
            {getProcessingLabel(
              recording.status,
              showSyntheticProgress ? syntheticPercent : recording.uploadProgressPercent
            )}
          </StyledProcessingButton>
        )}
        {recording.isFinished && !recording.isProcessing && (
          <MDSButton variant={MDSButtonVariant.OnAccent} onClick={onClear}>
            Cancel
          </MDSButton>
        )}
      </LeftActions>
      <RightActions>
        <AudioCaptureWaveform waveform={recording.waveform} />
        <StyledDurationText
          $durationLabel={recording.durationLabel}
          variant={MDSTextVariant.TextSm}
          color={MDSTextColor.Emphasis}
        >
          {recording.durationLabel}
        </StyledDurationText>

        {!showRetryUpload && !showRetryTransform && (
          <MDSTooltip
            config={
              recording.noteExistsRemotely
                ? undefined
                : {
                    label: "Cannot process voice recording until note is synced.",
                    placement: MDSTooltipPlacement.Top,
                  }
            }
          >
            <MDSButton
              variant={MDSButtonVariant.Contrast}
              onClick={handleDoneClick}
              isDisabled={disableFinishButton}
            >
              Done
            </MDSButton>
          </MDSTooltip>
        )}

        {showRetryUpload && (
          <MDSButton
            variant={MDSButtonVariant.Contrast}
            onClick={() => {
              void onRetryUpload();
            }}
            aria-label="Retry upload"
          >
            Retry
          </MDSButton>
        )}

        {showRetryTransform && (
          <MDSButton
            variant={MDSButtonVariant.Contrast}
            onClick={() => {
              void onRetryTransform();
            }}
            aria-label="Retry processing"
          >
            Retry
          </MDSButton>
        )}
      </RightActions>
    </StyledButtonsContainer>
  );
}

const StyledButtonsContainer = styled.div<{ $showSeparator?: boolean }>`
  display: flex;
  flex-direction: row;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing["2"]};

  ${({ theme, $showSeparator }) =>
    $showSeparator &&
    `
  padding-top: ${theme.spacing[4]};
  border-top: 1px solid ${theme.colors.border.default}`};
`;

/**
 * The z-index of the `LeftActions` is one higher than the `RightActions`.
 * The goal is so that it can overlap the waveform component correctly without
 * causing distortion.
 */
const LeftActions = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  flex: 0;
  background-color: ${({ theme }) => theme.colors.surface.modal};
  padding-right: ${({ theme }) => theme.spacing[2]};
  z-index: 1;
`;

const RightActions = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  flex: 0;
  z-index: 0;
`;

/**
 * We use `durationLabel` to calculate the width because:
 * - we want the text to avoid "bumping" to the left
 * - but we also want to ensure that it shifts left as the duration increases past 99:99
 */
const StyledDurationText = styled(MDSText)<{ $durationLabel: string }>`
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
  text-align: left;
  padding-right: ${({ theme }) => theme.spacing[0]};
  padding-left: ${({ theme }) => theme.spacing[4]};
  width: ${({ $durationLabel, theme }) =>
    `calc(${$durationLabel.length * 8}px + ${theme.spacing["8"]})`};
  min-width: ${({ $durationLabel, theme }) =>
    `calc(${$durationLabel.length * 8}px + ${theme.spacing["8"]})`};
`;

const StyledIconButton = styled(MDSIconButton)`
  & svg {
    font-size: 18px;
  }
`;

const StyledResumeButton = styled(MDSButton)`
  min-width: 88px;
  background-color: ${({ theme }) => theme.colors.surface.modal};
`;

const StyledProcessingButton = styled(MDSButton)`
  pointer-events: none;
  background-color: ${({ theme }) => theme.colors.surface.modal} !important;
`;
